/**
 * Chamadas HTTP dos dias da escala.
 *
 * Papel: aqui ficam as chamadas de `/api/calendario/dias` que a tela usa —
 * listar um período, salvar um dia, limpar um dia e limpar um período — e a
 * cópia de uma semana inteira, em `/api/calendario/semanas/copiar`.
 *
 * Como usar: a listagem exige o recorte inteiro, com casa e as duas pontas do
 * período, e devolve **só os dias que têm registro**. Um mês recém-aberto volta
 * como array vazio, e é `celulasDoMes` que desenha a grade mesmo assim.
 *
 * `salvarDia` não recebe identificador: o dia é encontrado por casa e data, e a
 * API cria ou substitui conforme já exista. Quem chama não precisa saber em
 * qual dos dois casos está.
 */
import {
  type CopiaDaSemana,
  type DiaCalendario,
  type NovoDiaCalendario,
  type RecorteDoCalendario,
  diaCalendarioSchema,
} from '@concierge/shared';
import { z } from 'zod';

import { clienteApi } from '@/shared/api';

/** A listagem inteira, sem envelope de paginação. */
const listaDeDiasSchema = z.array(diaCalendarioSchema);

/**
 * Lê os dias com registro num período, do mais antigo para o mais recente.
 *
 * @param recorte - A casa e as duas pontas do período, ambas inclusivas.
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns Os dias que têm alguma coisa marcada, cada um com as situações e a
 *   escala aninhadas.
 */
export function buscarDias(
  recorte: RecorteDoCalendario,
  sinal?: AbortSignal,
): Promise<DiaCalendario[]> {
  return clienteApi.get('/calendario/dias', {
    schema: listaDeDiasSchema,
    params: recorte,
    sinal,
  });
}

/**
 * Grava um dia, criando-o ou substituindo o que havia.
 *
 * As situações e a escala enviadas substituem as anteriores por inteiro: o que
 * não vier no corpo deixa de existir no dia. É o que permite tirar alguém da
 * escala sem uma rota própria para isso.
 *
 * @param dados - O dia preenchido, já validado.
 * @returns O dia como ficou gravado, com a residência e a escala aninhadas.
 */
export function salvarDia(dados: NovoDiaCalendario): Promise<DiaCalendario> {
  return clienteApi.put('/calendario/dias', dados, { schema: diaCalendarioSchema });
}

/**
 * Apaga um dia inteiro, com as situações e a escala dele.
 *
 * Não recebe schema porque a API responde 204 sem corpo. O dia volta a ser o dia
 * em branco que a grade desenha para qualquer data sem registro.
 *
 * @param id - Identificador da linha em `calendario_dias`.
 */
export function excluirDia(id: string): Promise<void> {
  return clienteApi.remover(`/calendario/dias/${encodeURIComponent(id)}`);
}

/**
 * Apaga todos os dias de um período, com as situações e a escala de cada um.
 *
 * É o "Limpar mês inteiro": uma instrução só no banco, que apaga tudo ou nada.
 * Férias e diárias não são dias, e continuam.
 *
 * @param recorte - A casa e as duas pontas do período, ambas inclusivas.
 */
export function limparPeriodo(recorte: RecorteDoCalendario): Promise<void> {
  return clienteApi.remover('/calendario/dias', { params: recorte });
}

/**
 * Copia os sete dias de uma semana sobre os sete de outra.
 *
 * A API grava a cópia inteira numa transação: os dias de destino são apagados e
 * regravados, e as férias e diárias marcadas são gravadas de novo deslocadas.
 * Uma falha no meio não deixa a semana copiada pela metade.
 *
 * @param dados - A casa, o primeiro dia de cada semana e os períodos marcados.
 */
export function copiarSemana(dados: CopiaDaSemana): Promise<void> {
  return clienteApi.enviar('POST', '/calendario/semanas/copiar', dados);
}
